import React from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Layout Components
import PublicNavbar from './components/layout/PublicNavbar';
import PublicFooter from './components/layout/PublicFooter';
import PortalLayout from './components/layout/PortalLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Public Pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import ServicesPage from './pages/public/ServicesPage';
import ProjectsPage from './pages/public/ProjectsPage';
import ContactPage from './pages/public/ContactPage';
import TeamPage from './pages/public/TeamPage';
import AcademyPage from './pages/public/AcademyPage';
import TrainingDetailPage from './pages/public/TrainingDetailPage';
import PaymentConfirmationPage from './pages/public/PaymentConfirmationPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Portal Pages
import DashboardPage from './pages/portal/DashboardPage';
import ClientsPage from './pages/portal/ClientsPage';
import ClientProfilePage from './pages/portal/ClientProfilePage';
import PortalProjectsPage from './pages/portal/ProjectsPage';
import ProjectDetailPage from './pages/portal/ProjectDetailPage';
import NotificationsPage from './pages/portal/NotificationsPage';
import SearchPage from './pages/portal/SearchPage';
import AuditLogPage from './pages/portal/AuditLogPage';
import ContactsPage from './pages/portal/ContactsPage';
import UsersPage from './pages/portal/UsersPage';
import MessagesPage from './pages/portal/MessagesPage';
import ArchivesPage from './pages/portal/ArchivesPage';

// CMS Pages
import CmsDashboard from './pages/cms/CmsDashboard';
import CmsServices from './pages/cms/CmsServices';
import CmsFormations from './pages/cms/CmsFormations';
import CmsTeam from './pages/cms/CmsTeam';
import CmsProjects from './pages/cms/CmsProjects';
import CmsPartners from './pages/cms/CmsPartners';
import CmsMedia from './pages/cms/CmsMedia';
import CmsPages from './pages/cms/CmsPages';
import CmsSettings from './pages/cms/CmsSettings';

// Academy Admin Pages
import AcademyLayout, { AcademyDashboard } from './pages/academy/AcademyLayout';
import AcademyTrainings from './pages/academy/AcademyTrainings';
import AcademyCategories from './pages/academy/AcademyCategories';
import AcademyInstructors from './pages/academy/AcademyInstructors';
import AcademyRegistrations from './pages/academy/AcademyRegistrations';
import AcademyPayments from './pages/academy/AcademyPayments';
import AcademySessions from './pages/academy/AcademySessions';
import AcademyCertificates from './pages/academy/AcademyCertificates';
import AcademyAnalytics from './pages/academy/AcademyAnalytics';
import AcademySettings from './pages/academy/AcademySettings';
import AcademyLMS from './pages/academy/AcademyLMS';

// Public Layout Wrapper
const PublicLayout = () => (
  <div className="min-h-screen flex flex-col">
    <PublicNavbar />
    <main className="flex-1">
      <Outlet />
    </main>
    <PublicFooter />
  </div>
);

// Portal Layout Wrapper
const PortalWrapper = () => (
  <ProtectedRoute>
    <PortalLayout>
      <Outlet />
    </PortalLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/projets" element={<ProjectsPage />} />
              <Route path="/equipe" element={<TeamPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/academie" element={<AcademyPage />} />
              <Route path="/academie/formation/:slug" element={<TrainingDetailPage />} />
            </Route>

            {/* Academy payment confirmation (no footer/navbar) */}
            <Route path="/academie/confirmation" element={<PaymentConfirmationPage />} />

            {/* Auth Routes */}
            <Route path="/connexion" element={<LoginPage />} />
            <Route path="/inscription" element={<RegisterPage />} />

            {/* Portal Routes */}
            <Route path="/portail" element={<PortalWrapper />}>
              <Route index element={<DashboardPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/:id" element={<ClientProfilePage />} />
              <Route path="projets" element={<PortalProjectsPage />} />
              <Route path="projets/:id" element={<ProjectDetailPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="recherche" element={<SearchPage />} />
              <Route path="journal" element={<AuditLogPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="utilisateurs" element={<UsersPage />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="archives" element={<ArchivesPage />} />
            </Route>

            {/* CMS Routes */}
            <Route path="/cms" element={<ProtectedRoute><CmsDashboard /></ProtectedRoute>} />
            <Route path="/cms/pages" element={<ProtectedRoute><CmsPages /></ProtectedRoute>} />
            <Route path="/cms/services" element={<ProtectedRoute><CmsServices /></ProtectedRoute>} />
            <Route path="/cms/formations" element={<ProtectedRoute><CmsFormations /></ProtectedRoute>} />
            <Route path="/cms/team" element={<ProtectedRoute><CmsTeam /></ProtectedRoute>} />
            <Route path="/cms/projects" element={<ProtectedRoute><CmsProjects /></ProtectedRoute>} />
            <Route path="/cms/partners" element={<ProtectedRoute><CmsPartners /></ProtectedRoute>} />
            <Route path="/cms/media" element={<ProtectedRoute><CmsMedia /></ProtectedRoute>} />
            <Route path="/cms/settings" element={<ProtectedRoute><CmsSettings /></ProtectedRoute>} />

            {/* Academy Admin Routes */}
            <Route path="/academy" element={<ProtectedRoute><AcademyLayout /></ProtectedRoute>}>
              <Route index element={<AcademyDashboard />} />
              <Route path="formations" element={<AcademyTrainings />} />
              <Route path="categories" element={<AcademyCategories />} />
              <Route path="instructeurs" element={<AcademyInstructors />} />
              <Route path="inscriptions" element={<AcademyRegistrations />} />
              <Route path="sessions" element={<AcademySessions />} />
              <Route path="certificats" element={<AcademyCertificates />} />
              <Route path="paiements" element={<AcademyPayments />} />
              <Route path="analytique" element={<AcademyAnalytics />} />
              <Route path="parametres" element={<AcademySettings />} />
              <Route path="lms" element={<AcademyLMS />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
